"use client";

import { LoadingCirclesHoriz } from "@frontend/components/elements/LoadingCirclesHoriz";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { Form } from "@frontend/shared/components/ui/form";
import { cn } from "@frontend/shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import type { ComplexFormProps, Component } from "../../[clientSlug]/types";
import { renderChildren } from "../components";

interface ComplexFormComponentProps {
  style: string;
  props: ComplexFormProps;
  formChildren: Component[];
  params: { clientSlug: string; orgId: number };
}

// Dynamic schema builder based on form props
const createFormSchema = (sendEmail: boolean, sendPhone: boolean) => {
  const baseSchema = {
    body: z.string().optional(),
  };

  const conditionalFields = {
    ...{ email: z.string().email("Please enter a valid email address") },
    ...{ phone: z.string().min(12, "Phone number is required") },
    ...{ name: z.string().min(1, "Name is required") },
  };

  return z.object({
    ...baseSchema,
    ...conditionalFields,
  });
};

export function ComplexFormComponent({
  style,
  props,
  formChildren,
  params,
}: ComplexFormComponentProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  const { data: organizationAssets, isLoading: isLoadingOrganizationAssets } =
    useQuery({
      queryKey: ["organizationAssets", params.orgId],
      queryFn: () => getOrganizationAssetsForOrg(params.orgId),
      enabled: !!params.orgId,
    });
  const organizationEmail = organizationAssets?.email;
  const organizationPhone = organizationAssets?.phoneNumber;

  const {
    formId,
    formName,
    formDescription,
    sendPhone,
    sendEmail,
    subject,
    successMessage,
    endpoint,
  } = props;

  // Create dynamic schema based on form requirements
  const formSchema = createFormSchema(sendEmail, sendPhone);
  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...(sendEmail && { email: "" }),
      ...(sendPhone && { phone: "" }),
      ...{ name: "" },
      body: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const requestBody: Record<string, unknown> = {};

      if (organizationEmail) {
        requestBody.email = organizationEmail;
      }
      if (organizationPhone) {
        requestBody.phone = organizationPhone;
      }
      if (subject) {
        requestBody.subject = subject;
      }

      requestBody.body = `
        Hello! A visitor has submitted the following general inquiry:\n
        Name: ${data.name ? data.name : "Name was not filled out"}\n
        Email: ${data.email ? data.email : "Email was not filled out"}\n
        Phone: ${data.phone ? data.phone : "Phone was not filled out"}\n
        Message: ${data.body ? data.body : "Message not filled out"}\n
      `;

      if (!endpoint) throw new Error("No endpoint provided");

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        toast.success(
          successMessage || "Your message has been sent successfully!",
        );
        form.reset();
      } else {
        throw new Error(result.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to send message. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingOrganizationAssets) return <LoadingCirclesHoriz />;

  if (!sendEmail && !sendPhone) return null;
  if (sendEmail && !organizationEmail) return null;
  if (sendPhone && !organizationPhone) return null;

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn(style)}
      >
        {formName && (
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">{formName}</h2>
            {formDescription && (
              <p className="text-sm text-muted-foreground">{formDescription}</p>
            )}
          </div>
        )}

        {formChildren &&
          renderChildren(formChildren, {
            clientSlug: params.clientSlug,
            orgId: params.orgId,
          })}
      </form>
    </Form>
  );
}
